/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named the other console.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, all of which both consoles need identically.
 * It is a copy rather than a shared package because this repository builds every package in
 * `packages/` through three explicit steps in the root `package.json`, and a sixth package that
 * `npm run check` could not build before its consumers would be worse than this duplication.
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` records the decision, what it costs, and the
 * precondition for undoing it.
 */
/**
 * Who is using the console, and which tenant they are looking at.
 *
 * Both are read from the access token, never chosen by the application. The
 * scopes decide what is drawn and the tenant decides what is asked for, and a
 * console that let either be set locally would be deciding its own
 * authorization — which `apps/README.md` forbids, and which every service would
 * refuse anyway.
 *
 * The tenant deserves its own paragraph. A platform operator can cross the
 * tenant boundary, the design commission requires that they always know which
 * side they are on, and the consequence of getting it wrong is not a wrong
 * screen — it is an operator cancelling a real trip belonging to a company
 * they have never heard of. So the tenant is never implicit: it is part of the
 * session, it is rendered in the chrome, and switching it is an act rather than
 * a side effect of navigation.
 */

export interface Session {
  readonly subject: string;
  readonly displayName: string;
  readonly scopes: ReadonlySet<string>;
  readonly tenantId: string;
  readonly tenantName: string;
  /** True when this actor may act across tenants. Changes what the chrome says. */
  readonly crossTenant: boolean;
  /**
   * The operator's own identifier, where the subject is one.
   *
   * Ten services compare `actor.subject` against a domain identifier — a customer, a driver, a rider,
   * a user — and each of those is a uuid, so the platform's convention is that a token's `sub` *is*
   * the identifier of the person it belongs to. The header's notification bell needs it, because
   * NTF-05 addresses an inbox as `/v1/tenants/{tenantId}/users/{userId}/…` and refuses unless the
   * subject equals that path.
   *
   * Undefined when the subject is not a uuid. That is not a fallback to guess around: an inbox
   * addressed with the wrong identifier is another operator's inbox, and the header shows no count
   * rather than the wrong one.
   */
  readonly userId?: string;
  /**
   * What the avatar shows. Derived from the display name, which is presentation rather than identity.
   */
  readonly initials: string;
}

const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Two letters for the avatar: the first of the first and last words, or the first two of one word.
 *
 * Upper-cased, because the design draws them upper-case, and taken from the display name rather than
 * from the subject — an operator recognises their own initials and would not recognise a uuid's.
 */
function initialsOf(displayName: string): string {
  const words = displayName
    .split(/[\s._-]+/)
    .map((word) => word.trim())
    .filter((word) => /^[A-Za-z]/.test(word));
  if (words.length === 0) return displayName.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words.at(-1)![0]}`.toUpperCase();
}

interface AccessTokenClaims {
  readonly sub?: unknown;
  readonly name?: unknown;
  readonly scope?: unknown;
  readonly tenant_id?: unknown;
}

/**
 * Read a session out of an access token, without trusting it for anything but
 * display.
 *
 * The signature is verified by the services, not here — a browser cannot hold
 * the key, and a client that "validated" a token would be performing a check
 * whose failure it would also be responsible for reporting. What this does is
 * read the claims so the console can draw the right chrome and hide what the
 * operator cannot use, and every request still carries the token to a service
 * that checks it properly.
 */
export function sessionFromClaims(
  claims: AccessTokenClaims,
  tenantName: string,
  options: { readonly crossTenant?: boolean } = {},
): Session | undefined {
  const subject = typeof claims.sub === "string" ? claims.sub : undefined;
  const tenantId = typeof claims.tenant_id === "string" ? claims.tenant_id : undefined;
  if (subject === undefined || tenantId === undefined) return undefined;

  const scope = claims.scope;
  const scopes = Array.isArray(scope)
    ? scope.filter((value): value is string => typeof value === "string")
    : typeof scope === "string"
      ? scope.split(" ").filter((value) => value.length > 0)
      : [];

  const displayName =
    typeof claims.name === "string" && claims.name.length > 0 ? claims.name : subject;

  return {
    subject,
    displayName,
    scopes: new Set(scopes),
    tenantId,
    tenantName,
    crossTenant: options.crossTenant ?? false,
    ...(UUID.test(subject) ? { userId: subject } : {}),
    initials: initialsOf(displayName),
  };
}
